import React, { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/lib/auth'

import AppLayout from '@/components/layout/AppLayout'
import SignIn from '@/pages/auth/SignIn'
import SignUp from '@/pages/auth/SignUp'
import AcceptInvite from '@/pages/auth/AcceptInvite'
import AssignmentRespond from '@/pages/vendor/AssignmentRespond'
import Dashboard from '@/pages/Dashboard'
import AutomationHealth from '@/pages/AutomationHealth'

import Inbox from '@/pages/ops/Inbox'
import ConversationDetail from '@/pages/ops/ConversationDetail'
import TeamChat from '@/pages/team/TeamChat'

import Contacts from '@/pages/crm/Contacts'
import ContactDetail from '@/pages/crm/ContactDetail'
import Companies from '@/pages/crm/Companies'
import CompanyDetail from '@/pages/crm/CompanyDetail'
import Vendors from '@/pages/crm/Vendors'
import VendorDetail from '@/pages/crm/VendorDetail'
import Deals from '@/pages/crm/Deals'
import SalesCalendar from '@/pages/crm/SalesCalendar'
import Tasks from '@/pages/crm/Tasks'
import DealDetail from '@/pages/crm/DealDetail'

import Quotes from '@/pages/payments/Quotes'
import QuoteDetail from '@/pages/payments/QuoteDetail'
import Invoices from '@/pages/payments/Invoices'
import InvoiceDetail from '@/pages/payments/InvoiceDetail'
import RecurringInvoices from '@/pages/payments/RecurringInvoices'
import RecurringInvoiceDetail from '@/pages/payments/RecurringInvoiceDetail'
import Products from '@/pages/payments/Products'

import ProjectsHub from '@/pages/projects/ProjectsHub'
import FleetPage from '@/pages/fleet/FleetPage'
import VehicleDetail from '@/pages/fleet/VehicleDetail'
import EquipmentDetail from '@/pages/fleet/EquipmentDetail'
import IncidentsPage from '@/pages/incidents/IncidentsPage'
import IncidentDetail from '@/pages/incidents/IncidentDetail'
import FilmRoute from '@/pages/public/landing/FilmScene'
import ProjectDetail from '@/pages/projects/ProjectDetail'

import FieldHub from '@/pages/field/FieldHub'
import JobDetail from '@/pages/field/JobDetail'
import ReputationHub from '@/pages/reputation/ReputationHub'
import IntegrationsHub from '@/pages/integrations/IntegrationsHub'
import IntegrationDetail from '@/pages/integrations/IntegrationDetail'
import Settings from '@/pages/settings/Settings'
import Analytics from '@/pages/Analytics'
import PipelineForecast from '@/pages/reports/PipelineForecast'
import MarketingHub from '@/pages/marketing/MarketingHub'
import AIHub from '@/pages/ai/AIHub'

import Onboarding from '@/pages/onboarding/Onboarding'

import Landing from '@/pages/public/Landing'
import Terms from '@/pages/public/Terms'
import Privacy from '@/pages/public/Privacy'
import MeetJoin from '@/pages/meet/MeetJoin'
import Refund from '@/pages/public/Refund'

import PortalLogin from '@/pages/portal/PortalLogin'
import PortalSetPassword from '@/pages/portal/PortalSetPassword'
import PortalLayout from '@/pages/portal/PortalLayout'
import PortalJobs from '@/pages/portal/PortalJobs'
import PortalHistory from '@/pages/portal/PortalHistory'
import PortalQuotes from '@/pages/portal/PortalQuotes'
import PortalInvoices from '@/pages/portal/PortalInvoices'
import PortalLocations from '@/pages/portal/PortalLocations'
import PortalRequests from '@/pages/portal/PortalRequests'
import PortalSpend from '@/pages/portal/PortalSpend'
import PortalJobDetail from '@/pages/portal/PortalJobDetail'
import PortalTeam from '@/pages/portal/PortalTeam'
import PortalSettings from '@/pages/portal/PortalSettings'
import VendorLogin from '@/pages/vendor/VendorLogin'
import VendorSetPassword from '@/pages/vendor/VendorSetPassword'
import VendorLayout from '@/pages/vendor/VendorLayout'
import VendorJobs from '@/pages/vendor/VendorJobs'
import VendorJobDetail from '@/pages/vendor/VendorJobDetail'
import VendorHistory from '@/pages/vendor/VendorHistory'
import VendorTeam from '@/pages/vendor/VendorTeam'
import VendorSettings from '@/pages/vendor/VendorSettings'
import { VendorThemeProvider } from '@/lib/vendorTheme'
import { PortalThemeProvider } from '@/lib/portalTheme'
import RequestsInbox from '@/pages/requests/RequestsInbox'
import GatedRoute from '@/components/GatedRoute'

function LoadingScreen() {
  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #7c3aed, #6d28d9)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 32px rgba(124,58,237,0.4)' }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M13 3L4 14h9l-2 7 9-11h-9l2-7z" fill="white" />
        </svg>
      </div>
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, workspace, portalUser, vendorUser, sessionResolved } = useAuth()
  if (!sessionResolved) return <LoadingScreen />
  if (!user) return <Navigate to="/signin" replace />
  // Portal users routed to /portal — they have a valid session but no
  // workspace/profile, so the staff app would render with all queries
  // disabled or, worse, trigger the no-profile signOut in auth.ts.
  if (portalUser) return <Navigate to="/portal" replace />
  // Vendor sessions route to their own home (V2a's /vendor/jobs).
  if (vendorUser) return <Navigate to="/vendor" replace />
  if (workspace && !workspace.onboarding_completed_at) return <Navigate to="/onboarding" replace />
  return <>{children}</>
}

// Portal guard — symmetric to ProtectedRoute, just inverted: requires
// a portal session, redirects staff to /app and signed-out users to
// /portal/login. Vendor sessions are isolated here too.
function PortalProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, portalUser, vendorUser, sessionResolved } = useAuth()
  if (!sessionResolved) return <LoadingScreen />
  if (!user) return <Navigate to="/portal/login" replace />
  if (vendorUser) return <Navigate to="/vendor" replace />
  if (!portalUser) return <Navigate to="/app" replace />
  return <>{children}</>
}

// Vendor guard — symmetric to PortalProtectedRoute. Vendor session
// required to enter /vendor/*; staff bounce to /app; portal clients
// bounce to /portal; signed-out users to /vendor/login.
function VendorProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, profile, portalUser, vendorUser, sessionResolved } = useAuth()
  if (!sessionResolved) return <LoadingScreen />
  if (!user) return <Navigate to="/vendor/login" replace />
  if (profile) return <Navigate to="/app" replace />
  if (portalUser) return <Navigate to="/portal" replace />
  if (!vendorUser) return <Navigate to="/vendor/login" replace />
  return <>{children}</>
}

// Admin-only vendor route (e.g., /vendor/team). Techs bounce to
// /vendor/jobs — Team is admin-only.
function VendorAdminRoute({ children }: { children: React.ReactNode }) {
  const { vendorUser } = useAuth()
  if (!vendorUser) return <Navigate to="/vendor/login" replace />
  if (vendorUser.role !== 'admin') return <Navigate to="/vendor/jobs" replace />
  return <>{children}</>
}

// Admin-only portal route (e.g., /portal/team). Members and viewers
// bounce to /portal/jobs — Team is a hidden-and-gated surface.
function PortalAdminRoute({ children }: { children: React.ReactNode }) {
  const { portalUser } = useAuth()
  if (!portalUser) return <Navigate to="/portal/login" replace />
  if (portalUser.role !== 'admin') return <Navigate to="/portal/jobs" replace />
  return <>{children}</>
}

// Non-viewer portal route — money surfaces (Quotes, Invoices, Spend).
// Viewers bounce to /portal/jobs. portal_read on quotes/invoices already
// returns 0 rows for viewers at the DB level (Phase 6a), but the route
// guard short-circuits before the page renders an empty state.
function PortalNonViewerRoute({ children }: { children: React.ReactNode }) {
  const { portalUser } = useAuth()
  if (!portalUser) return <Navigate to="/portal/login" replace />
  if (portalUser.role === 'viewer') return <Navigate to="/portal/jobs" replace />
  return <>{children}</>
}

// Owner-only route (e.g., /app/automation-health). Non-owners bounce to
// /app — ops/infrastructure visibility is owner-scoped. Mirrors the
// VendorAdminRoute / PortalAdminRoute pattern.
function OwnerRoute({ children }: { children: React.ReactNode }) {
  const { profile } = useAuth()
  if (!profile?.is_owner) return <Navigate to="/app" replace />
  return <>{children}</>
}

// Onboarding-only: authenticated users who haven't completed onboarding.
// Redirects to / if already done.
function OnboardingRoute({ children }: { children: React.ReactNode }) {
  const { user, workspace, sessionResolved } = useAuth()
  if (!sessionResolved) return <LoadingScreen />
  if (!user) return <Navigate to="/signin" replace />
  if (workspace?.onboarding_completed_at) return <Navigate to="/app" replace />
  return <>{children}</>
}

function AuthRoute({ children }: { children: React.ReactNode }) {
  const { user, portalUser, sessionResolved } = useAuth()
  if (!sessionResolved) return <LoadingScreen />
  // Signed-in staff → /app; signed-in portal → /portal. Public auth
  // routes (signin / signup) shouldn't show themselves to any session.
  if (user && portalUser) return <Navigate to="/portal" replace />
  if (user) return <Navigate to="/app" replace />
  return <>{children}</>
}

export default function App() {
  const { initialize } = useAuth()
  useEffect(() => {
    let subscription: { unsubscribe: () => void } | undefined
    initialize().then(sub => { if (sub) subscription = sub })
    return () => { subscription?.unsubscribe() }
  }, [])

  // TEMP: public marketing/pricing hidden while we focus on the demo experience.
  // Flip to true to restore the public landing page (which also contains the
  // pricing section). Landing stays imported + intact on disk — restore is just
  // this one boolean. See also: Landing.tsx (unchanged), Settings.tsx in-app
  // billing is separate and unaffected.
  const SHOW_PUBLIC_MARKETING = false

  return (
    <Routes>
      {/* Public marketing + policy pages */}
      <Route path="/" element={SHOW_PUBLIC_MARKETING ? <Landing /> : <Navigate to="/signin" replace />} />
      {/* Local-review route for the superseded landing page (branch
          feature/orkestra-landing-page-local). Renders the new landing
          unconditionally so it can be reviewed at /preview without changing
          SHOW_PUBLIC_MARKETING's behavior at "/". Remove when the flag is
          promoted and "/" owns the page directly. */}
      <Route path="/preview" element={<Landing />} />
      {/* Local capture route: renders only the animated product film at
          1280×720 for recording to WebM (scripts/landing-film.mjs). Branch-
          local; remove alongside /preview when promoting. */}
      <Route path="/preview-film" element={<FilmRoute />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/refund" element={<Refund />} />

      {/* Orkestra Meetings — public guest video join (CP2). Anonymous-reachable;
          the unguessable :roomToken is validated server-side by meeting-guest-token. */}
      <Route path="/meet/:roomToken" element={<MeetJoin />} />

      {/* Auth */}
      <Route path="/signin" element={<AuthRoute><SignIn /></AuthRoute>} />
      <Route path="/signup" element={<AuthRoute><SignUp /></AuthRoute>} />
      {/* AcceptInvite intentionally NOT wrapped in AuthRoute — invitee is
          typically signed-out, but the page also handles already-signed-in
          callers internally. */}
      <Route path="/auth/accept-invite" element={<AcceptInvite />} />

      {/* Vendor assignment accept/decline — unauthenticated, token-bearing.
          The vendor-respond function is deployed --no-verify-jwt; the token
          in the URL is the credential. NOT wrapped in any auth gate. */}
      <Route path="/vendor/assignment/accept"  element={<AssignmentRespond action="accept"  />} />
      <Route path="/vendor/assignment/decline" element={<AssignmentRespond action="decline" />} />

      {/* Vendor Portal V2a — login + set-password landing + authenticated
          shell. Mirrors the client portal route shape. VendorThemeProvider
          wraps each entry so the data-theme + brand tinting live on the
          /vendor wrapper with their own 'orkestra-vendor-theme' storage
          key. Index redirects to /vendor/jobs. */}
      <Route path="/vendor/login" element={<VendorThemeProvider><VendorLogin /></VendorThemeProvider>} />
      <Route path="/vendor/set-password" element={<VendorThemeProvider><VendorSetPassword /></VendorThemeProvider>} />
      <Route path="/vendor" element={<VendorThemeProvider><VendorProtectedRoute><VendorLayout /></VendorProtectedRoute></VendorThemeProvider>}>
        <Route index element={<Navigate to="/vendor/jobs" replace />} />
        <Route path="jobs"    element={<VendorJobs />} />
        <Route path="jobs/:assignmentId" element={<VendorJobDetail />} />
        <Route path="history" element={<VendorHistory />} />
        <Route path="team"    element={<VendorAdminRoute><VendorTeam /></VendorAdminRoute>} />
        <Route path="settings" element={<VendorSettings />} />
      </Route>

      {/* Onboarding */}
      <Route path="/onboarding" element={<OnboardingRoute><Onboarding /></OnboardingRoute>} />

      {/* Customer Portal — token-gated login + read-only client surface.
          PortalLogin / PortalSetPassword are unauthenticated landing pages
          (anyone can hit them; the actions inside reject without a valid
          token/session). Everything under /portal/* below the index goes
          through PortalProtectedRoute (requires a portal session).
          PortalThemeProvider wraps each entry so all three (login,
          set-password, /portal/*) get element-scoped data-theme with the
          portal-namespaced storage key — see lib/portalTheme.tsx for why
          this is separate from the staff ThemeProvider in main.tsx. */}
      <Route path="/portal/login" element={<PortalThemeProvider><PortalLogin /></PortalThemeProvider>} />
      <Route path="/portal/set-password" element={<PortalThemeProvider><PortalSetPassword /></PortalThemeProvider>} />
      <Route path="/portal" element={<PortalThemeProvider><PortalProtectedRoute><PortalLayout /></PortalProtectedRoute></PortalThemeProvider>}>
        <Route index element={<Navigate to="/portal/jobs" replace />} />
        <Route path="jobs" element={<PortalJobs />} />
        <Route path="jobs/:id" element={<PortalJobDetail />} />
        <Route path="quotes" element={<PortalNonViewerRoute><PortalQuotes /></PortalNonViewerRoute>} />
        <Route path="invoices" element={<PortalNonViewerRoute><PortalInvoices /></PortalNonViewerRoute>} />
        <Route path="locations" element={<PortalLocations />} />
        <Route path="requests" element={<PortalRequests />} />
        <Route path="spend" element={<PortalNonViewerRoute><PortalSpend /></PortalNonViewerRoute>} />
        <Route path="team" element={<PortalAdminRoute><PortalTeam /></PortalAdminRoute>} />
        <Route path="settings" element={<PortalSettings />} />
        <Route path="history" element={<PortalHistory />} />
      </Route>

      {/* Main app */}
      <Route path="/app" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />

        <Route path="inbox" element={<GatedRoute feature="inbox"><Inbox /></GatedRoute>} />
        <Route path="inbox/:id" element={<GatedRoute feature="inbox"><ConversationDetail /></GatedRoute>} />
        <Route path="requests" element={<RequestsInbox />} />
        <Route path="team-chat" element={<Navigate to="/app/team-chat/general" replace />} />
        <Route path="team-chat/dm/:dmId" element={<TeamChat />} />
        <Route path="team-chat/group/:groupId" element={<TeamChat />} />
        <Route path="team-chat/:channelName" element={<TeamChat />} />

        <Route path="contacts" element={<GatedRoute feature="contacts"><Contacts /></GatedRoute>} />
        <Route path="contacts/:id" element={<GatedRoute feature="contacts"><ContactDetail /></GatedRoute>} />
        <Route path="companies" element={<GatedRoute feature="contacts"><Companies /></GatedRoute>} />
        <Route path="companies/:id" element={<GatedRoute feature="contacts"><CompanyDetail /></GatedRoute>} />
        <Route path="vendors" element={<GatedRoute feature="contacts"><Vendors /></GatedRoute>} />
        <Route path="vendors/:id" element={<GatedRoute feature="contacts"><VendorDetail /></GatedRoute>} />
        <Route path="deals" element={<GatedRoute feature="deals"><Deals /></GatedRoute>} />
        <Route path="tasks" element={<GatedRoute feature="deals"><Tasks /></GatedRoute>} />
        <Route path="deals/:id" element={<GatedRoute feature="deals"><DealDetail /></GatedRoute>} />
        <Route path="sales-calendar" element={<GatedRoute feature="deals"><SalesCalendar /></GatedRoute>} />

        <Route path="quotes" element={<GatedRoute feature="quotes"><Quotes /></GatedRoute>} />
        <Route path="quotes/:id" element={<GatedRoute feature="quotes"><QuoteDetail /></GatedRoute>} />
        <Route path="invoices" element={<GatedRoute feature="invoices"><Invoices /></GatedRoute>} />
        <Route path="invoices/recurring" element={<GatedRoute feature="invoices"><RecurringInvoices /></GatedRoute>} />
        <Route path="invoices/recurring/:id" element={<GatedRoute feature="invoices"><RecurringInvoiceDetail /></GatedRoute>} />
        <Route path="invoices/:id" element={<GatedRoute feature="invoices"><InvoiceDetail /></GatedRoute>} />
        <Route path="products" element={<GatedRoute features={['quotes', 'invoices', 'field']}><Products /></GatedRoute>} />

        <Route path="projects" element={<GatedRoute feature="projects"><ProjectsHub /></GatedRoute>} />
        <Route path="projects/:id" element={<GatedRoute feature="projects"><ProjectDetail /></GatedRoute>} />

        <Route path="fleet" element={<GatedRoute feature="fleet"><FleetPage /></GatedRoute>} />
        <Route path="fleet/vehicles/:id" element={<GatedRoute feature="fleet"><VehicleDetail /></GatedRoute>} />
        <Route path="fleet/equipment/:id" element={<GatedRoute feature="fleet"><EquipmentDetail /></GatedRoute>} />
        {/* Incidents reuse the 'field' entitlement (field operations); no billing-fn edit warranted. */}
        <Route path="incidents" element={<GatedRoute feature="field"><IncidentsPage /></GatedRoute>} />
        <Route path="incidents/:id" element={<GatedRoute feature="field"><IncidentDetail /></GatedRoute>} />
        <Route path="field" element={<GatedRoute feature="field"><FieldHub /></GatedRoute>} />
        <Route path="jobs/:id" element={<GatedRoute feature="field"><JobDetail /></GatedRoute>} />
        <Route path="reputation" element={<GatedRoute feature="reputation"><ReputationHub /></GatedRoute>} />

        <Route path="integrations" element={<GatedRoute feature="integrations"><IntegrationsHub /></GatedRoute>} />
        <Route path="integrations/:key" element={<GatedRoute feature="integrations"><IntegrationDetail /></GatedRoute>} />

        <Route path="marketing" element={<GatedRoute feature="marketing"><MarketingHub /></GatedRoute>} />
        <Route path="ai" element={<GatedRoute feature="ai"><AIHub /></GatedRoute>} />

        <Route path="analytics" element={<GatedRoute feature="analytics"><Analytics /></GatedRoute>} />
        <Route path="reports" element={<GatedRoute feature="analytics"><PipelineForecast /></GatedRoute>} />
        <Route path="automation-health" element={<OwnerRoute><AutomationHealth /></OwnerRoute>} />
        <Route path="settings/*" element={<Settings />} />
      </Route>

      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  )
}
